import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { ForbiddenPage } from '@/components/errors/ForbiddenPage';
import { PageLoading } from '@/components/loading/PageLoading';
import { env } from '@/config/env';
import { PATHS } from '@/config/paths';
import { useEffectiveMenuPermissions } from '@/features/auth/hooks/useEffectiveMenuPermissions';
import { useGetMyMenuTreeQuery } from '@/features/menus/api/menusApi';
import { menuTreeHasExactPath } from '@/features/menus/utils/menuTreeFilters';
import { useAppSelector } from '@/hooks/useAppSelector';

import { canAccessOpsNavItem } from './filterOpsNav';
import { getDynamicOpsLeafPaths } from './opsMenuPaths';
import { OPS_NAV_ITEMS } from './opsNav.config';

/** Route Ops chỉ cần guard tĩnh (role/permission), không bắt buộc xuất hiện trong menu BE. */
const STATIC_OPS_NAV_KEYS = new Set(['permission-matrix']);

interface RequireOpsRouteProps {
  /** Key matching {@link OPS_NAV_ITEMS} `key`. */
  navKey: string;
  children: ReactNode;
}

/**
 * Route guard: có `VITE_API_BASE_URL` → chỉ cho phép khi path nằm trên menu BE (leaf `/ops/...`).
 * Không gọi được API menu (dev cục bộ) → fallback rule tĩnh ({@link canAccessOpsNavItem}).
 */
export function RequireOpsRoute({ navKey, children }: RequireOpsRouteProps) {
  const user = useAppSelector((s) => s.auth.user);
  const { data: menuPermissions } = useEffectiveMenuPermissions();
  const skipMenu = !user || !env.API_BASE_URL;
  const {
    data: menuTree,
    isFetching: menuFetching,
    isError: menuError,
  } = useGetMyMenuTreeQuery(undefined, {
    skip: skipMenu,
  });

  const item = OPS_NAV_ITEMS.find((i) => i.key === navKey);

  const dynamicPaths = useMemo(
    () => (menuError ? [] : getDynamicOpsLeafPaths(menuTree)),
    [menuTree, menuError],
  );

  const staticOk = Boolean(item && user && canAccessOpsNavItem(user, item, menuPermissions));
  const hasIotStationsMenu = useMemo(
    () => menuTreeHasExactPath(menuTree, PATHS.IOT_STATIONS),
    [menuTree],
  );
  /**
   * Mục nav cha (vd. `/ops/reports`) hợp lệ khi menu BE có ÍT NHẤT một path con `/ops/reports/<slug>`.
   * Tránh bị 403 sau khi admin thêm menu con khiến path cha không còn là leaf.
   */
  const itemPath = item?.path;
  const hasDynamicChildUnderItem = itemPath
    ? dynamicPaths.some((p) => p.startsWith(`${itemPath}/`))
    : false;

  const dynamicOk = Boolean(
    item &&
    (dynamicPaths.includes(item.path) ||
      hasDynamicChildUnderItem ||
      (navKey === 'stations' && hasIotStationsMenu) ||
      (navKey === 'sensor-types' && hasIotStationsMenu)),
  );

  if (!user || !item) {
    return <ForbiddenPage />;
  }

  /** Dev / không cấu hình gateway: giữ guard tĩnh. */
  if (skipMenu && staticOk) {
    return <>{children}</>;
  }

  if (!skipMenu && menuFetching && !dynamicOk) {
    return (
      <div style={{ padding: 24 }}>
        <PageLoading />
      </div>
    );
  }

  const staticNavOnlyOk = Boolean(item && STATIC_OPS_NAV_KEYS.has(navKey) && staticOk);

  if (!skipMenu && (dynamicOk || staticNavOnlyOk)) {
    return <>{children}</>;
  }

  return <ForbiddenPage />;
}
